// src/helpers/systemConfigCache.ts
// Cache-first loader cho SystemConfig (SystemService/GetSystemConfig) — dùng chung giữa
// Login, System > Configuration, Issuance Plans, tạo đại lý, LookupPortal...
// Đọc chi tiết thiết kế trong README.md (mục 3.1).

import { CacheService } from 'services/cache';
import { CacheConfig } from 'services/cache/CacheConfig';
import { getSystemConfig } from 'helpers/fakebackend_helper';
import { setConfig } from 'slices/systemConfiguration/reducer';
import { fetchSystemConfig } from 'slices/systemConfiguration/thunk';
import { SystemConfiguration } from 'pages/SystemConfiguration/types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('helper_system_config_cache');

/**
 * Nạp SystemConfig vào Redux store theo kiểu stale-while-revalidate:
 * - Cache hit: đổ ngay bản cache vào store để render tức thì, RỒI luôn gọi lại API ngầm
 *   (không chặn UI) để cập nhật store + ghi đè cache.
 * - Cache miss/hết hạn: gọi API qua thunk (có loading/error state) rồi ghi cache.
 *
 * Mục 8.1, README_UPDATE.md — trước đây cache hit chỉ đổ bản cũ vào store rồi dừng, khiến
 * mọi trang đọc state.SystemConfiguration.config thấy cấu hình cũ tới hết TTL (30 phút) khi
 * admin khác đổi cấu hình từ máy/session khác — write-through lúc lưu chỉ cập nhật localStorage
 * của chính máy đang sửa, không lan sang máy khác. Giờ mỗi lần load đều revalidate ngầm.
 */
export const loadSystemConfigWithCache = async (dispatch: any): Promise<void> => {
  const cacheKey = CacheConfig.SYSTEM_CONFIG.key;
  const cacheTtl = CacheConfig.SYSTEM_CONFIG.ttl;

  const cached = CacheService.get<SystemConfiguration>(cacheKey);

  if (cached.cached && cached.data) {
    // Render tức thì với bản cache
    dispatch(setConfig(cached.data));

    // Revalidate ngầm — gọi thẳng API (không qua thunk để tránh bật cờ loading gây nháy UI).
    // Lỗi mạng thì refreshInBackground tự nuốt, giữ nguyên bản cache đang hiển thị.
    CacheService.refreshInBackground(
      cacheKey,
      async () => {
        const response: any = await getSystemConfig();
        const fresh = (response?.data ?? response) as SystemConfiguration;
        if (!fresh) {
          // Ném lỗi để refreshInBackground bỏ qua, không ghi đè cache bằng dữ liệu rỗng
          throw new Error(st('systemconfig_rong_khi_revalidate_message'));
        }
        dispatch(setConfig(fresh));
        return fresh;
      },
      cacheTtl,
    );
    return;
  }

  const result = await dispatch(fetchSystemConfig());
  if (result.payload) {
    CacheService.set(cacheKey, result.payload, cacheTtl);
  }
};
